import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Block,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  Icon,
} from "../../../components/Component";
import { Input } from "reactstrap";
import http from "../../../utils/http-common";
import { getPublicUrl } from "../../../utils/Utils";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import moment from "moment";
import TeleconsultationPageShell from "../../../components/admin/TeleconsultationPageShell";

const TYPE_LABEL = {
  calendar_setup: "Calendar setup",
  technical: "Technical",
  billing: "Billing",
  other: "Other",
};

const STATUS_LABEL = {
  pending: "Pending",
  in_progress: "In progress",
  resolved: "Resolved",
};

export default function PartnerSupportRequestsList() {
  const history = useHistory();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");

  const load = useCallback(
    (signal) => {
      setLoading(true);
      setErr("");
      const params = {};
      if (status) params.status = status;
      if (type) params.type = type;
      http
        .get("/api/v1/admin/support-requests", { params, signal })
        .then((res) => {
          const list = res.data?.data;
          setRows(Array.isArray(list) ? list : []);
        })
        .catch((e) => {
          if (isAbortError(e)) return;
          setErr(e.response?.data?.error || "Failed to load support requests");
          setRows([]);
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [status, type]
  );

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  return (
    <TeleconsultationPageShell
      headTitle="Partner support requests"
      title="Partner support requests"
      description="Requests submitted from the partner portal (availability calendar setup, technical, billing)."
    >
      <Block>
        <div className="admin-list-filters">
          <div className="admin-list-filters__field">
            <label className="form-label small text-soft">Status</label>
            <Input type="select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In progress</option>
              <option value="resolved">Resolved</option>
            </Input>
          </div>
          <div className="admin-list-filters__field">
            <label className="form-label small text-soft">Type</label>
            <Input type="select" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">All</option>
              <option value="calendar_setup">Calendar setup</option>
              <option value="technical">Technical</option>
              <option value="billing">Billing</option>
              <option value="other">Other</option>
            </Input>
          </div>
          <div className="admin-list-filters__actions">
            <Button color="light" outline type="button" onClick={() => load()}>
              <Icon name="refresh" />
              <span className="ms-1">Refresh</span>
            </Button>
          </div>
        </div>

        {err && (
          <p className="text-danger small" role="alert">
            {err}
          </p>
        )}

        <div className="table-responsive">
          <DataTable className="card-stretch">
            <DataTableBody>
              <DataTableHead>
                <DataTableRow size="sm">
                  <span className="sub-text">Date</span>
                </DataTableRow>
                <DataTableRow size="md">
                  <span className="sub-text">Partner</span>
                </DataTableRow>
                <DataTableRow size="sm">
                  <span className="sub-text">Type</span>
                </DataTableRow>
                <DataTableRow size="sm">
                  <span className="sub-text">Status</span>
                </DataTableRow>
                <DataTableRow className="nk-tb-col-tools text-end">
                  <span className="sub-text">Actions</span>
                </DataTableRow>
              </DataTableHead>

              {loading ? (
                <DataTableItem>
                  <DataTableRow className="text-center py-4">Loading…</DataTableRow>
                </DataTableItem>
              ) : rows.length === 0 ? (
                <DataTableItem>
                  <DataTableRow className="text-center text-soft py-4">
                    No requests match the filters.
                  </DataTableRow>
                </DataTableItem>
              ) : (
                rows.map((r) => {
                  const pid = r.partnerId && typeof r.partnerId === "object" ? r.partnerId : null;
                  const partnerLabel =
                    pid?.organization_name ||
                    pid?.contact_person_name ||
                    (r.partnerId && String(r.partnerId)) ||
                    "—";
                  return (
                    <DataTableItem key={String(r._id)}>
                      <DataTableRow size="sm" dataLabel="Date">
                        {moment(r.createdAt).format("YYYY-MM-DD HH:mm")}
                      </DataTableRow>
                      <DataTableRow size="md" dataLabel="Partner">
                        {partnerLabel}
                      </DataTableRow>
                      <DataTableRow size="sm" dataLabel="Type">
                        {TYPE_LABEL[r.type] || r.type}
                      </DataTableRow>
                      <DataTableRow size="sm" dataLabel="Status">
                        {STATUS_LABEL[r.status] || r.status}
                      </DataTableRow>
                      <DataTableRow className="nk-tb-col-tools text-end" dataLabel="Actions">
                        <Button
                          size="sm"
                          color="primary"
                          outline
                          type="button"
                          onClick={() =>
                            history.push(getPublicUrl(`teleconsultations/support-requests/${r._id}`))
                          }
                        >
                          View
                        </Button>
                      </DataTableRow>
                    </DataTableItem>
                  );
                })
              )}
            </DataTableBody>
          </DataTable>
        </div>
      </Block>
    </TeleconsultationPageShell>
  );
}
